import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { Lock, AlertTriangle, CheckCircle, Eye, EyeOff, XCircle } from 'lucide-react';

const ChangePassword = ({ onClose, forceChange = false }) => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  useEffect(() => {
    checkPasswordStrength(formData.newPassword);
  }, [formData.newPassword]);

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const isPasswordValid = () => {
    return Object.values(passwordStrength).every(v => v === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.oldPassword) {
      toast.error('Please enter your current password');
      return;
    }

    if (!isPasswordValid()) {
      toast.error('Password does not meet security requirements');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/auth/change-password', {
        old_password: formData.oldPassword,
        new_password: formData.newPassword
      });

      toast.success('Password changed successfully!');
      setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      
      if (onClose) {
        setTimeout(() => onClose(), 1500);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      
      // Handle validation errors from backend
      let errorMessage = 'Failed to change password';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail.msg) {
          errorMessage = detail.msg;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-pwd-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !forceChange) onClose?.();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        transition={{ duration: 0.22 }}
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-6 border-b border-gray-100 bg-[#171717] rounded-t-[1.25rem]">
          <div className="p-2.5 bg-white/10 rounded-xl">
            <Lock className="w-5 h-5 text-gray-300" aria-hidden="true" />
          </div>
          <div>
            <h2 id="change-pwd-title" className="text-lg font-bold text-white">Change Password</h2>
            {forceChange && (
              <p className="text-xs text-amber-300 flex items-center gap-1 mt-0.5">
                <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                Password change required to continue
              </p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
          {/* Current Password */}
          <div>
            <label htmlFor="old-password" className="block text-sm font-medium text-gray-700 mb-1.5">
              Current Password
            </label>
            <div className="relative">
              <input
                id="old-password"
                type={showOldPassword ? 'text' : 'password'}
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                className="login-input pr-10"
                placeholder="Enter current password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowOldPassword(!showOldPassword)}
                aria-label={showOldPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showOldPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1.5">
              New Password
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className="login-input pr-10"
                placeholder="Enter new password"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1.5">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="login-input pr-10"
                placeholder="Confirm new password"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
              </button>
            </div>
          </div>

          {/* Requirements */}
          {formData.newPassword && (
            <div className="bg-slate-50 rounded-xl p-4 space-y-1.5" role="list" aria-label="Password requirements">
              <p className="text-xs font-semibold text-gray-600 mb-2">Password Requirements</p>
              {[
                { key: 'length',    text: 'At least 8 characters' },
                { key: 'uppercase', text: 'One uppercase letter' },
                { key: 'lowercase', text: 'One lowercase letter' },
                { key: 'number',    text: 'One number' },
                { key: 'special',   text: 'One special character (!@#$%...)' },
              ].map(({ key, text }) => (
                <div key={key} className="flex items-center gap-2" role="listitem">
                  {passwordStrength[key]
                    ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" aria-hidden="true" />
                    : <XCircle className="w-3.5 h-3.5 text-gray-300 shrink-0" aria-hidden="true" />}
                  <span className={`text-xs ${passwordStrength[key] ? 'text-emerald-600' : 'text-gray-500'}`}>{text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Match indicator */}
          {formData.confirmPassword && (
            <div className="flex items-center gap-2" role="status" aria-live="polite">
              {formData.newPassword === formData.confirmPassword
                ? <><CheckCircle className="w-4 h-4 text-emerald-500" aria-hidden="true" /><span className="text-xs text-emerald-600">Passwords match</span></>
                : <><AlertTriangle className="w-4 h-4 text-amber-500" aria-hidden="true" /><span className="text-xs text-amber-600">Passwords do not match</span></>}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            {!forceChange && (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading || !isPasswordValid() || formData.newPassword !== formData.confirmPassword}
              className="flex-1 px-4 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ChangePassword;
